import * as React from "react";
import {
  CellContext,
  createColumnHelper
} from "@tanstack/react-table";

import { Text } from "src/library/components/Text";
import { customCreateColumnHelper } from "src/core/dashboard/components/widgets/table/customColumnHelper";
import { formatPrice } from "src/core/prices/utils/price-helpers";
import {
  Transaction,
  TransactionStatus,
  TransactionType
} from "src/core/accounting/api/accounting.api";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import AccountingStatusBadge from "src/core/accounting/components/AccountingStatusBadge";
import { formatCustomerName } from "src/core/customers/utils/format-customer-name";
import { formatDropName } from "src/core/drops/utils/format-drop-name";

import { formatTransactionType } from "./transactionTableColumns";

export interface IAdminTransactionTableColumns {
  actionButton: (info: CellContext<Transaction, unknown>) => React.ReactNode;
  terminology: ITerminology;
}

export const adminTransactionTableColumns = (params: IAdminTransactionTableColumns) => {
  const customColumnHelper = customCreateColumnHelper<Transaction>();
  const columnHelper = createColumnHelper<Transaction>();

  // Date column
  const dateColumn = customColumnHelper.dateColumn(columnHelper, "transactionDate", {
    header: "Date",
    enableSorting: true
  });

  // Customer column (admin only)
  const customerColumn = customColumnHelper.standardTextColumn(columnHelper, "customer", {
    header: params.terminology.customer.singular.text,
    enableSorting: false,
    enableColumnFilter: true,
    cell: (info: CellContext<Transaction, unknown>) => (
      <Text>
        {info.row.original.customer ? formatCustomerName(info.row.original.customer) : "-"}
      </Text>
    ),
    meta: {
      style: { align: "left" },
      filter: {
        filterType: "string",
        filterOperation: "none",
        minLength: 3
      }
    }
  });

  // Branch column (admin only)
  const branchColumn = customColumnHelper.standardTextColumn(columnHelper, "branch", {
    header: params.terminology.drop.singular.text,
    enableSorting: false,
    enableColumnFilter: true,
    cell: (info: CellContext<Transaction, unknown>) => (
      <Text>
        {info.row.original.branch ? formatDropName(info.row.original.branch) : "-"}
      </Text>
    ),
    meta: {
      style: { align: "left" },
      filter: {
        filterType: "string",
        filterOperation: "none",
        minLength: 3
      }
    }
  });

  // Amount column
  const amountColumn = customColumnHelper.priceColumn(columnHelper, "amount", {
    header: "Amount",
    cell: ({ getValue }) => {
      const amount = getValue() as number;

      return (
        <Text>
          {formatPrice(amount)}
        </Text>
      );
    }
  }, "GBP");

  // Type column (Credit / Payment)
  const typeColumn = customColumnHelper.standardTextColumn(columnHelper, "type", {
    id: "type",
    header: "Type",
    enableColumnFilter: true,
    cell: ({ getValue }) => {
      const type = getValue() as TransactionType;

      return (
        <Text>
          {formatTransactionType(type)}
        </Text>
      );
    }
  });

  // Status badge column
  const statusColumn = customColumnHelper.standardTextColumn(columnHelper, "status", {
    header: "Status",
    enableColumnFilter: true,
    cell: ({ getValue }) => {
      const status = getValue() as TransactionStatus;

      return (
        <AccountingStatusBadge
          variant="transaction"
          status={status}
          compact
        />
      );
    }
  });

  // Actions column
  const actionsColumn = customColumnHelper.actionColumn(columnHelper, params.actionButton, { size: 150 });
  const externalIdColumn = customColumnHelper.standardTextColumn(columnHelper, "externalId", { header: "Transaction Number" });

  // Build columns array - admin view includes customer and branch
  const columns = [
    externalIdColumn,
    dateColumn,
    customerColumn,
    branchColumn,
    amountColumn,
    typeColumn,
    statusColumn,
    actionsColumn
  ];

  return columns;
};
